/**
 * ================================================================================================
 *  THE SUPPORT BOARD — the reader the queue arrives with
 * ================================================================================================
 *
 * The Owner, 2026-11-04:
 *
 *     *"מנהל התמיכה יוכל לשבץ מטפל ולהעלות לפיתוח. הלוח יציג מספר פנייה, מצב, מטפל, מועד פתיחה,
 *      יעד תגובה והפרות SLA."*
 *
 * ── IT SHIPS IN THE SAME RELEASE AS THE QUEUE, AND THAT IS NOT A COINCIDENCE ──────────────────
 *
 * `SupportAssistant`'s old mail path carried the reason: *"until somebody watches it, a form that
 * swallows a customer's problem is worse than no form."* A board that arrived a release later would
 * be exactly that form. So this screen is the condition that comment set, met.
 *
 * ── THE BREACH IS READ AGAINST THE SERVER'S CLOCK AND NEVER THE BROWSER'S ─────────────────────
 *
 * `asOf` travels with the answer. A board that compared `respondByAt` against `Date.now()` would
 * report a breach on a laptop whose clock is wrong — and *"the SLA is broken"* is not a thing to be
 * wrong about in front of the person whose job it measures.
 *
 * ── AND IT NEVER SAYS A TICKET IS ANSWERED BECAUSE IT IS CLOSED ───────────────────────────────
 *
 * The SLA column reads `firstResponseAt`. A ticket somebody replied to and left open is ON TIME; a
 * ticket closed without a word is NOT. Reading `closedAt` instead would make "closed quickly" look
 * like "answered quickly", which is the one number on this board somebody might be tempted to
 * improve.
 */

import { Fragment, useCallback, useState } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { ServerRead } from '../components/ServerRead';
import { InfoNote } from '../components/InfoNote';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { callOperation } from '../api/operations';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
import { useServerData } from '../api/useServerData';
import { formatInstantHe } from '../api/adminVocabulary';
import type { TermHe } from '../api/adminVocabulary';
import type { SupportTicket } from '../api/contract';

const TITLE_HE = 'תור התמיכה';

/**
 * THE FIVE STATES, IN HEBREW, WITH THE TONE THE CHIP CARRIES.
 *
 * A CLOSED VOCABULARY AND NO FALLBACK LABEL. A state this build has no word for is shown as its
 * own code rather than as a plausible Hebrew word, so a vocabulary that grew on the server is
 * visible here instead of being quietly mislabelled — which is the shape the Owner met three times
 * in two days on his own screens.
 */
const STATE_HE: Readonly<Record<string, TermHe>> = {
  open: { labelHe: 'פתוחה', tone: 'warn' },
  assigned: { labelHe: 'משובצת', tone: 'muted' },
  escalated: { labelHe: 'הועלתה לפיתוח', tone: 'warn' },
  answered: { labelHe: 'נענתה', tone: 'ok' },
  closed: { labelHe: 'נסגרה', tone: 'muted' },
};

/*
 * `TermHe` AND THE FOUR TONES THE DESIGN SYSTEM HAS. My first draft invented `bad` and `idle`,
 * which are not in `Tone` -- and a tone the chip does not know would have rendered unstyled, which
 * is the class-that-styles-nothing defect this programme met on the billing table last week.
 */
function stateChip(state: string): TermHe {
  return STATE_HE[state] ?? { labelHe: state, tone: 'muted' };
}

/**
 * HAS THE TARGET BEEN MISSED?
 *
 * ── THREE ANSWERS AND NOT TWO ────────────────────────────────────────────────────────────────
 *
 *   answered in time   there is a first response, and it was before the target
 *   BREACHED           the target has passed and nothing was answered before it
 *   still within       the target has not passed yet
 *
 * A ticket ANSWERED LATE is a breach that already happened and stays one: folding it into
 * *"answered"* would let a board look clean the day after it was not. That is why the comparison is
 * against `firstResponseAt` when there is one and against `asOf` when there is not, rather than
 * against whichever is available.
 */
function slaOf(ticket: SupportTicket, asOf: string): TermHe {
  const target = Date.parse(ticket.respondByAt);
  if (ticket.firstResponseAt !== null) {
    const answered = Date.parse(ticket.firstResponseAt);
    return answered <= target
      ? { labelHe: 'נענתה בזמן', tone: 'ok' }
      : { labelHe: 'נענתה באיחור', tone: 'error' };
  }
  return Date.parse(asOf) > target
    ? { labelHe: 'חריגה מיעד התגובה', tone: 'error' }
    : { labelHe: 'בתוך היעד', tone: 'ok' };
}

export function PlatformSupportQueueScreen(): JSX.Element {
  const ctx = useWorkContext();
  /*
   * THE MOVES ARE WITHHELD WITHOUT `platform.support.ticket.assign`, not offered and refused. The
   * operator and the auditor READ this board under a different capability; a row of buttons that
   * always answer 403 teaches them the product is broken.
   */
  const capabilities = ctx.session?.capabilities ?? [];
  const mayMove = capabilities.includes('platform.support.ticket.assign');
  /*
   * THE TARGET IS THE OWNER'S ALONE. `platform.support.policy.set` is granted to `platform_owner`
   * and nobody else, so the support manager who works the queue cannot change the number their own
   * work is measured against — which is the point of it being a setting rather than a constant.
   */
  const maySetTarget = capabilities.includes('platform.support.policy.set');

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }
  return <SupportQueueBody mayMove={mayMove} maySetTarget={maySetTarget} />;
}

/**
 * THE BODY, BELOW THE SURFACE GUARD.
 *
 * Every hook lives here for `PlatformTenantScreen`'s reason: `ctx.surface` is `null` until
 * `auth.whoami.read` answers, so a guard above hooks in ONE component gives React two different
 * hook orders for one component and throws during render. The split is also what keeps a PLATFORM
 * read from being issued on a wrong-surface render.
 */
function SupportQueueBody({
  mayMove,
  maySetTarget,
}: {
  readonly mayMove: boolean;
  readonly maySetTarget: boolean;
}): JSX.Element {
  const [showAll, setShowAll] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  /** Which ticket's answer panel is open. One at a time: two open is the wall again. */
  const [answering, setAnswering] = useState<string | null>(null);

  /*
   * ── ONE READ, TWO LISTS, AND THE TOGGLE IS A CHOICE BETWEEN THEM ─────────────────────
   *
   * The server bounds the live queue and the recent history SEPARATELY, so switching between them
   * is not a filter over a truncated page — which would have reported *"no open tickets"* about a
   * 200-row page of closed ones. The thunk therefore does not depend on the toggle, and no request
   * is re-issued when it flips.
   *
   * AND IT CARRIES NO QUERY STRING, because `callOperation` has none: the choke point declares
   * `params`, `body` and `signal`. Widening the one function every call in this application goes
   * through, for one screen's toggle, would be the wrong place to spend a change — so the answer
   * carries both lists instead.
   */
  const read = useCallback(() => callOperation('platform.support_ticket.read', {}), []);
  const board = useServerData(read);

  async function move(
    ticketUid: string,
    verb: 'assign' | 'escalate' | 'answer' | 'close',
  ): Promise<void> {
    setMoving(ticketUid);
    setFailure(null);
    try {
      await callOperation('platform.support_ticket.move', {
        params: { ticket_uid: ticketUid },
        /*
         * `handlerIsMe: true` ON EVERY ASSIGN, because that is all this release can do: the move
         * route takes a boolean and not a user id. It is LESS than the Owner asked for and the
         * server's own comment says so -- naming somebody else needs a reader for the list of
         * platform staff, which is another capability and another route.
         */
        body: { move: verb, handlerIsMe: true },
      });
      board.reload();
    } catch (err) {
      setFailure(classifyActionError(err));
    } finally {
      setMoving(null);
    }
  }

  return (
    <AppShell titleHe={TITLE_HE} screenId="platform-support-queue">
      {failure === null ? null : <ActionFailureNotice failure={failure} />}

      <p className="actions-row">
        <button type="button" className="btn" onClick={() => setShowAll(!showAll)}>
          {showAll ? 'חזרה לפניות הפתוחות' : 'הצג פניות שנענו או נסגרו'}
        </button>
      </p>

      <ServerRead result={board.result} whatHe="את תור התמיכה" skeletonRows={5}>
        {(data) => (
          <>
            <TargetPanel
              hours={data.respondWithinHours}
              mayEdit={maySetTarget}
              onSet={() => board.reload()}
            />
            {(showAll ? data.recent : data.tickets).length === 0 ? (
              <StateBlock
                titleHe={showAll ? 'אין פניות סגורות' : 'אין פניות פתוחות'}
                bodyHe={
                  showAll
                    ? 'טרם נענתה או נסגרה פנייה.'
                    : 'כל הפניות נענו או נסגרו.'
                }
                tone="empty"
              />
            ) : (
              <div className="grid">
                <div className="grid__scroll">
                  <table className="grid__table">
                    <thead className="grid__head">
                      <tr>
                        <th className="grid__th grid__th--identity" scope="col">מספר פנייה</th>
                        <th className="grid__th" scope="col">לקוח</th>
                        <th className="grid__th" scope="col">נושא</th>
                        <th className="grid__th" scope="col">מצב</th>
                        <th className="grid__th" scope="col">מטפל</th>
                        <th className="grid__th" scope="col">מועד פתיחה</th>
                        <th className="grid__th" scope="col">יעד תגובה</th>
                        <th className="grid__th" scope="col">SLA</th>
                        <th className="grid__th" scope="col">פעולה</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(showAll ? data.recent : data.tickets).map((ticket) => {
                        const chip = stateChip(ticket.state);
                        const sla = slaOf(ticket, data.asOf);
                        const busy = moving === ticket.ticketUid;
                        return (
                          <Fragment key={ticket.ticketUid}>
                          <tr>
                            <td className="grid__td grid__th--identity">
                              <span dir="ltr">{ticket.displayNo}</span>
                            </td>
                            <td className="grid__td">{ticket.tenantDisplayName}</td>
                            <td className="grid__td">
                              <span>{ticket.subject}</span>
                              {/*
                                THE WORDS ARE ON THE BOARD AND NOT BEHIND A CLICK. 09 §2.1 M4: a
                                modal is not where a person reads a row. A handler deciding what to
                                pick up needs the question, not its title.
                              */}
                              <p className="notice__body">{ticket.body}</p>
                              <p className="notice__body">
                                {ticket.openedByDisplayName === null
                                  ? ''
                                  : `נפתחה על ידי ${ticket.openedByDisplayName}. `}
                                {ticket.screenId === null ? '' : `מסך: ${ticket.screenId}. `}
                                {/*
                                  THE VERSION IS THE SERVER'S, and an absent one is said rather
                                  than left blank: a development host has no release variables set,
                                  and a blank cell reads as a field somebody forgot.
                                */}
                                {ticket.releaseIdentity === null
                                  ? 'גרסה לא נרשמה בשרת הזה.'
                                  : `גרסה: ${ticket.releaseIdentity}.`}
                              </p>
                            </td>
                            <td className="grid__td">
                              <StatusChip labelHe={chip.labelHe} tone={chip.tone} />
                            </td>
                            <td className="grid__td">
                              {ticket.handlerDisplayName ?? <span className="cell-null">—</span>}
                            </td>
                            <td className="grid__td">
                              <span dir="ltr">{formatInstantHe(ticket.openedAt)}</span>
                            </td>
                            <td className="grid__td">
                              <span dir="ltr">{formatInstantHe(ticket.respondByAt)}</span>
                            </td>
                            <td className="grid__td">
                              <StatusChip labelHe={sla.labelHe} tone={sla.tone} />
                            </td>
                            <td className="grid__td">
                              {!mayMove ? null : (
                                <div className="actions-row">
                                  {/*
                                    THE MOVES OFFERED ARE THE ONES LEGAL FROM THIS STATE, and the
                                    legality is the service's table rather than a second opinion
                                    here. A closed ticket offers nothing; an answered one may still
                                    be closed.
                                  */}
                                  {ticket.state === 'closed' ? (
                                    <span className="cell-null">—</span>
                                  ) : (
                                    <>
                                      {ticket.state === 'answered' ? null : (
                                        <>
                                          <button
                                            type="button"
                                            className="btn"
                                            disabled={busy}
                                            onClick={() => void move(ticket.ticketUid, 'assign')}
                                          >
                                            קח לטיפולי
                                          </button>
                                          {ticket.state === 'escalated' ? null : (
                                            <button
                                              type="button"
                                              className="btn"
                                              disabled={busy}
                                              onClick={() => void move(ticket.ticketUid, 'escalate')}
                                            >
                                              העלה לפיתוח
                                            </button>
                                          )}
                                          <button
                                            type="button"
                                            className="btn"
                                            disabled={busy}
                                            onClick={() => void move(ticket.ticketUid, 'answer')}
                                          >
                                            נענתה
                                          </button>
                                        </>
                                      )}
                                      {/*
                                        THE ANSWER COMES BEFORE THE CLOSE, in reading order and in
                                        the order of the work: a ticket is answered and then
                                        closed, and a board that offered the ending first would be
                                        offering the shortcut.
                                      */}
                                      <button
                                        type="button"
                                        className="btn btn--primary"
                                        onClick={() =>
                                          setAnswering(
                                            answering === ticket.ticketUid ? null : ticket.ticketUid,
                                          )
                                        }
                                      >
                                        כתיבת תשובה
                                      </button>
                                      <button
                                        type="button"
                                        className="btn"
                                        disabled={busy}
                                        onClick={() => void move(ticket.ticketUid, 'close')}
                                      >
                                        סגירה
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                          {/*
                            ── WHERE THE ANSWER IS WRITTEN. **0057, 2026-11-04.** ────────────────

                            **The Owner:** *"התשובות נכתבות בתוך GLPOINT ונשמרות בפנייה."*

                            UNTIL NOW `נענתה` WAS A BUTTON AND NOT AN ANSWER: it moved a state and
                            stamped a time, and whatever was actually said reached the customer by
                            telephone or somebody's own mailbox. The estate held the claim and not
                            the words.

                            IN ITS OWN ROW AND NOT IN A MODAL. 09 §2.1 M4 — a modal is not where a
                            person reads a row, and an answer is written while reading the question
                            above it. `colSpan` spans the board so the conversation is not squeezed
                            into whichever column happened to be last.
                          */}
                          {answering !== ticket.ticketUid ? null : (
                            <tr>
                              <td className="grid__td" colSpan={7}>
                                <SupportThread
                                  ticketUid={ticket.ticketUid}
                                  displayNo={ticket.displayNo}
                                  onAnswered={() => {
                                    setAnswering(null);
                                    board.reload();
                                  }}
                                />
                              </td>
                            </tr>
                          )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {/*
              THE CLOCK THE BREACHES WERE MEASURED AGAINST IS PRINTED. A board that showed a breach
              without saying when it was computed invites an argument nobody can settle.
            */}
            <p className="field__hint">
              {'נמדד מול שעון השרת: '}
              <span dir="ltr">{formatInstantHe(data.asOf)}</span>
            </p>
          </>
        )}
      </ServerRead>
    </AppShell>
  );
}

/**
 * THE RESPONSE TARGET, SHOWN TO EVERYBODY AND SET BY THE OWNER.
 *
 *     *"לא רוצה לראות מחיר בקוד אלא תחשיב לפי שדה להזנה שלי."*
 *
 * ── IT EXISTS BECAUSE A CONTROL FOUND ITS ABSENCE ───────────────────────────────────
 *
 * `platform.support_policy.set` was declared with its route, its capability and its request shape,
 * and NOTHING CALLED IT — which `reachability.test.ts` reports as a declared operation no screen
 * reaches, the *"written, correct, and reaching nothing"* shape. It found mine before the Owner
 * did.
 *
 * ── AND IT NEVER RE-TARGETS A TICKET ALREADY OPEN ────────────────────────────────
 *
 * `respond_by_at` is stamped on the ticket when it opens. The panel says so, because the natural
 * assumption about a target is the opposite — and one keystroke that breached or un-breached every
 * ticket on the board would make this number unusable as a measurement.
 */
function TargetPanel({
  hours,
  mayEdit,
  onSet,
}: {
  readonly hours: number | null;
  readonly mayEdit: boolean;
  readonly onSet: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [typed, setTyped] = useState(hours === null ? '' : String(hours));
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);

  /*
   * ── WHO SET IT, AND WHEN ────────────────────────────────────────────────────
   *
   * A SECOND READ FOR ONE FIELD, and it earns itself: the board's own answer carries the HOURS,
   * and this carries `updatedByDisplayName`, which 0055 seeds as NULL on purpose. *"Still the
   * default, nobody chose it"* and *"the Owner set it on this date"* are different facts about a
   * number that measures somebody's work, and a board that could not tell them apart would present
   * a seeded 24 as a decision.
   *
   * IT IS NOT FATAL. A refused or failing read leaves the panel showing the hours it already has
   * from the board — the provenance line is simply absent, which is honest, rather than the whole
   * target disappearing because one extra read did not land.
   */
  const readPolicy = useCallback(() => callOperation('platform.support_policy.read', {}), []);
  const policy = useServerData(readPolicy);
  const setBy = policy.result.state === 'loaded' ? policy.result.data : null;

  /* 0055's own CHECK: at least one hour, at most 2160. A target of zero is breached when it is set. */
  const value = Number(typed);
  const valid = /^\d+$/u.test(typed.trim()) && value >= 1 && value <= 2160;

  async function save(): Promise<void> {
    setSaving(true);
    setFailure(null);
    try {
      await callOperation('platform.support_policy.set', { body: { respondWithinHours: value } });
      setEditing(false);
      onSet();
      /* THE PROVENANCE LINE IS NOW WRONG until it is re-read: it still names the previous setter. */
      policy.reload();
    } catch (err) {
      setFailure(classifyActionError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-area="response-target">
      {failure === null ? null : <ActionFailureNotice failure={failure} />}
      <p className="field__hint">
        {hours === null
          ? /*
             * NO TARGET IS A STATE AND NOT A NUMBER. An estate missing 0055's seed has none, and a
             * board that printed a default would be measuring every ticket against a number nobody
             * chose.
             */
            'לא מוגדר יעד תגובה בשרת הזה, ולכן אין מול מה למדוד את הפניות.'
          : `יעד התגובה: ${String(hours)} שעות ממועד פתיחת הפנייה.`}
      </p>
      {setBy === null ? null : (
        <p className="field__hint">
          {setBy.updatedByDisplayName === null
            ? /*
               * NOBODY CHOSE IT, AND THAT IS SAID. 0055 seeds the row with `updated_by_user_id`
               * NULL so this sentence can exist: presenting a seeded 24 hours as somebody's
               * decision would make an argument about the number unresolvable.
               */
              'היעד הזה הוא ערך התחלה של המערכת, וטרם נקבע על ידי אדם.'
            : `נקבע על ידי ${setBy.updatedByDisplayName}.`}
        </p>
      )}
      {!mayEdit ? null : !editing ? (
        <p className="actions-row">
          <button type="button" className="btn" onClick={() => setEditing(true)}>
            שינוי יעד התגובה
          </button>
        </p>
      ) : (
        <div className="field-group">
          <label className="field">
            <span className="field__label">שעות עד תגובה ראשונה (רצופות)</span>
            <input
              className="field__input"
              name="support-respond-within-hours"
              inputMode="numeric"
              dir="ltr"
              value={typed}
              onChange={(e) => setTyped(e.currentTarget.value)}
            />
            {/*
              ── THE HINT WAS THE DATABASE'S CHECK, PRINTED AT A PERSON. **Owner, 2026-11-04.** ──
              *"הגדרת הזמן ל24 שעות עד 2160 לא ברורה, לא מבין מה זה."* It read *"between one hour
              and 2160 hours"*, which is `0055`'s constraint verbatim — and 2160 hours is NINETY
              DAYS. A range nobody would choose from, in a unit nobody counts in that far out.
              It now says the bound in the unit the number is actually set in, and the ceiling in
              the unit a person reads it in.
            */}
            <span className="field__hint">שעה אחת עד 90 יום. נספר ברצף, כולל לילות וסופי שבוע.</span>
            {/*
              THE CONSEQUENCE BELONGS IN COLLAPSIBLE HELP AND NOT IN A STANDING LINE.
              `noStandingProse.test.ts`: *"a screen carries a standing explanation. One statement
              fits in seventy characters; a paragraph belongs in collapsible help or in the
              assistant."* It fired on this very sentence, and it is right: the fact that a new
              target does not re-aim tickets already open is important and is not something the
              reader needs on screen every time they look at the number.
            */}
            <InfoNote summaryHe="מה המספר הזה קובע, ומה הוא לא">
              {/*
                ── FIRST RESPONSE IS NOT RESOLUTION, AND THE PRODUCT HOLDS ONLY ONE OF THEM ─────
                *"הצג שעתיים כיעד לתגובה ראשונה — בנפרד מזמן פתרון התקלה."*
                `core.support_policy` carries ONE number and the board measures it against
                `first_response_at`. A SECOND target, for resolution, is a second column and
                therefore a migration — it is not in this release, and saying so here is the
                alternative to a screen that lets a reader assume the number covers both.
              */}
              <p>
                <strong>זהו יעד לתגובה ראשונה בלבד</strong> — הזמן עד שמישהו אצלנו עונה לפנייה.
                הוא <strong>אינו</strong> יעד לפתרון התקלה. יעד נפרד לפתרון עדיין לא קיים
                במערכת, ולכן אין מה להציג לידו.
              </p>
              <p>
                הספירה <strong>רצופה</strong>: שעון, לא שעות עבודה. פנייה שנפתחת ב-17:00 עם יעד
                של שעתיים מגיעה ליעד ב-19:00 גם מחוץ לשעות הפעילות. יעד לפי שעות עבודה הוא לוח זמנים
                נפרד — אין כזה כאן.
              </p>
              <p>
                השינוי חל על פניות חדשות בלבד. יעד התגובה נרשם בכל פנייה ברגע שנפתחה,
                ואינו משתנה למפרע \u2014 כך שהקשה אחת אינה משנה את מצב ה-SLA של כל הלוח.
              </p>
            </InfoNote>
          </label>
          <div className="actions-row">
            <button
              type="button"
              className="btn btn--primary"
              disabled={!valid || saving}
              onClick={() => void save()}
            >
              {saving ? 'שומר\u2026' : 'שמירה'}
            </button>
            <button type="button" className="btn" onClick={() => setEditing(false)}>
              ביטול
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * ONE ENQUIRY'S CONVERSATION, AND THE BOX THE ANSWER IS TYPED IN.
 *
 * ── THE THREAD IS FETCHED WHEN THE PANEL OPENS ────────────────────────────────────
 *
 * Not with the board. A handler opens one enquiry out of a queue, and fetching every conversation
 * to draw a list would read every customer's words to render a table nobody asked it to.
 *
 * THE SEND IS DISABLED ON AN EMPTY BOX rather than refused after a press. The service refuses it
 * too — `reply_too_short` — and the database refuses it under that; this is the layer that stops
 * somebody discovering the rule by breaking it.
 */
function SupportThread({
  ticketUid,
  displayNo,
  onAnswered,
}: {
  readonly ticketUid: string;
  readonly displayNo: string;
  readonly onAnswered: () => void;
}): JSX.Element {
  const read = useCallback(
    () => callOperation('platform.support_ticket.thread', { params: { ticket_uid: ticketUid } }),
    [ticketUid],
  );
  const thread = useServerData(read);

  const [typed, setTyped] = useState('');
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);

  async function send(): Promise<void> {
    setSending(true);
    setFailure(null);
    try {
      await callOperation('platform.support_ticket.answer', {
        params: { ticket_uid: ticketUid },
        body: { body: typed },
      });
      setTyped('');
      /*
       * THE BOARD IS RELOADED BY THE CALLER, not here: answering stamps `first_response_at`, which
       * is what every breach reading on that board is measured against. A panel that refreshed
       * only itself would leave the row above it claiming the enquiry was still unanswered.
       */
      onAnswered();
    } catch (error: unknown) {
      setFailure(classifyActionError(error));
    } finally {
      setSending(false);
    }
  }

  return (
    <section data-area="support-thread">
      <h3 className="notice__title">
        {'התכתבות בפנייה '}
        <span dir="ltr">{displayNo}</span>
      </h3>

      <ServerRead result={thread.result} whatHe="את ההתכתבות בפנייה" skeletonRows={2}>
        {(data) => (
          <>
            {data.messages.length === 0 ? (
              /*
               * NOT AN ERROR AND NOT A BLANK. An enquiry nobody has written into yet is the
               * ordinary state of a new one, and the sentence says which.
               */
              <p className="notice__body">עדיין לא נכתבה תשובה בפנייה הזו.</p>
            ) : (
              data.messages.map((message) => (
                <article
                  key={message.messageUid}
                  className="thread__message"
                  data-side={message.authorSide === 'platform' ? 'platform' : 'office'}
                >
                  <p className="cell-note">
                    {message.authorSide === 'platform'
                      ? (message.authorDisplayName ?? 'צוות GL-POINT')
                      : (message.authorDisplayName ?? 'משתמש בארגון')}
                    {' · '}
                    <bdi>{formatInstantHe(message.createdAt)}</bdi>
                  </p>
                  <p className="thread__body">{message.body}</p>
                </article>
              ))
            )}
          </>
        )}
      </ServerRead>

      {failure === null ? null : <ActionFailureNotice failure={failure} />}

      <label className="field">
        <span className="field__label">תשובה ללקוח</span>
        <textarea
          className="field__input"
          rows={3}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
        />
        {/*
          WHAT HAPPENS WHEN IT IS SENT, SAID BEFORE IT IS — AND IN ONE STATEMENT.
          `noStandingProse` fired on the first version of this line at 82 characters, and it was
          right: the rule is that a standing line STATES and does not explain. What is left is the
          fact a handler needs before pressing. That the mail carries no answer text is in
          `SupportTicketUpdatedMail`, where the decision lives.
        */}
        <span className="field__hint">נשמרת בפנייה. הלקוח יקבל דואר עם קישור, בלי הנוסח.</span>
      </label>
      <div className="actions-row">
        <button
          type="button"
          className="btn btn--primary"
          disabled={sending || typed.trim() === ''}
          onClick={() => void send()}
        >
          {sending ? 'שולח…' : 'שליחת תשובה'}
        </button>
      </div>
    </section>
  );
}
